import {Injectable, inject} from '@angular/core';
import {HttpClient, HttpResponse} from '@angular/common/http';
import {ConfirmModalService} from '../gui/confirm-modal.service';
import {Observable, OperatorFunction} from 'rxjs';
import {Company} from '../../_data/admin/company';
import {map, tap} from 'rxjs/operators';
import {uEncodeURI} from '../../_utils/lang';
import {User} from '../../_data/admin/user';
import {mapErrorToHttpResponse, tapStatusToNotification} from '../../_utils/operators';
import {NotificationService} from '../gui/notification.service';
import {ApiEndpoints} from '../../_constants/api-endpoints';
import {isAdminRole, isUserRole} from '../../_utils/users';

@Injectable({
  providedIn: 'root'
})
export class CompaniesService {
  private http = inject(HttpClient);
  private modalService = inject(ConfirmModalService);
  private notificationService = inject(NotificationService);
  private apiEndpoints = inject(ApiEndpoints);


  private modifyCompanyErrorHandler(companyName: string): OperatorFunction<any, HttpResponse<string>> {
    return mapErrorToHttpResponse(this.notificationService, [
        {statusCode: 304, i18nMessage: 'content.company-not-updated', i18nData: {companyName: companyName}},
        {statusCode: 409, i18nMessage: 'content.company-already-exists', i18nData: {companyName: companyName}}
      ],
      {i18nMessage: 'content.company-not-created', i18nData: {companyName: companyName}}
    );
  }

  private addUpdateCompany(url: string, companyName: string): Observable<HttpResponse<string>> {
    return this.http.post(url, null, {observe: 'response', responseType: 'text'}).pipe(
      this.modifyCompanyErrorHandler(companyName),
      tap(httpResponse => {
        if(httpResponse.status !== 200) {
          this.notificationService.displayI18n('content.company-not-created', {companyName: companyName});
        }
      })
    );
  }

  addCompany(companyName: string, ipBypass: number): Observable<HttpResponse<string>> {
    return this.addUpdateCompany(`${this.apiEndpoints.companiesAdd}?companyName=${uEncodeURI(companyName)}&ipBypass=${ipBypass}`, companyName).pipe(
      tapStatusToNotification(
        this.notificationService,
        [{statusCode: 200, i18nMessage: 'content.company-created', i18nData: {companyName: companyName}}]
      )
    );
  }

  updateCompany(companyName: string, newCompanyName: string, ipBypass: number): Observable<HttpResponse<string>> {
    return this.addUpdateCompany(
      `${this.apiEndpoints.companiesUpdate}?companyName=${uEncodeURI(companyName)}&newCompanyName=${uEncodeURI(newCompanyName.toUpperCase())}&ipBypass=${ipBypass}`, companyName
    ).pipe(tapStatusToNotification(
      this.notificationService,
      [{statusCode: 200, i18nMessage: 'content.company-updated', i18nData: {companyName: companyName}}]
    ));
  }

  deleteCompany(companyName: string): Observable<HttpResponse<string>> {
    return this.http.delete(this.apiEndpoints.companiesDelete, {observe: 'response', responseType: 'text', body: {companyName: companyName}}).pipe(
      mapErrorToHttpResponse(this.notificationService, [{statusCode: 304, i18nMessage: 'content.branch-not-updated'}]),
      tapStatusToNotification(this.notificationService, [
        {statusCode: 200, i18nMessage: 'content.company-deleted', i18nData: {companyName: companyName}},
        {statusCode: 304, i18nMessage: 'content.company-not-deleted', i18nData: {companyName: companyName}}
      ])
    );
  }

  searchForCompany(companyName: string): Observable<Company[]> {
    return this.http.post<Company[]>(this.apiEndpoints.companiesSearch, {companyName: companyName});
  }

  getCompanyDetails(companyName: string): Observable<Company> {
    return this.http.post<Company>(this.apiEndpoints.getCompany, {companyName: companyName});
  }

  getCompanyUsers(companyName: string): Observable<{users: User[]; admins: User[]}> {
    return this.http.post<User[]>(this.apiEndpoints.companiesGetUsers, {companyName: companyName}).pipe(map(allUsers => {
      const users = allUsers?.filter(user => {
        const rolesSplit = user.roles.split(',');
        return rolesSplit.find(role => isUserRole(role)) && !rolesSplit.find(role => isAdminRole(role, 'admin'));
      }) ?? [];
      const admins = allUsers?.filter(user => user.roles.split(',').find(role => isAdminRole(role, 'admin'))) ?? [];
      return {users: users, admins: admins};
    }));
  }

  getCompanyAdmins(companyName: string): Observable<User[]> {
    return this.http.post<User[]>(this.apiEndpoints.userGetCompanyAdmins, {companyName: companyName});
  }

  confirmDeleteCompany(company: Company, ariaLabelledBy: string, container: HTMLElement, onDelete: (httpResponse: HttpResponse<string>) => any = () => {}) {
    const name = company.companyName;
    this.modalService.open(
      {i18nKey: 'content.delete-something', data: {what: name}},
      {i18nKey: 'content.confirm-delete-something', data: {what: name}},
      ariaLabelledBy,
      container
    ).result$.subscribe(accepted => {
      if(accepted) {
        console.debug(`OK clicked.  Terminating...`);
        this.deleteCompany(company.companyName).subscribe((httpResponse: HttpResponse<string>) => {
          console.debug(`Delete company http status: ${httpResponse.status}, body: ${httpResponse.body}`);
          onDelete(httpResponse);
        });
      }
    });
  }
}
